import type { AxiosRequestConfig } from "axios";

import { PaginationTable, TenantPerson } from "js/components/types";

import { AxiosInstance } from "../utils/AxiosInstance";
import { markExpectedBusinessResponse } from "../utils/expectedBusinessResponseMark";

export const getTenants = async () => {
  const { data } = await AxiosInstance.get(`/v1/tenants/persons`);
  return data;
};

export const getProperties = async (pagination?: PaginationTable) => {
  const params = pagination
    ? {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize,
      }
    : undefined;

  const { data } = await AxiosInstance.get(`/v1/tenants/properties`, {
    params,
  });
  return data;
};

export const getPropertiesUsages = async () => {
  const { data } = await AxiosInstance.get(`/v1/tenants/properties/usages`);
  return data;
};

export const createTenantReport = async (values: any) => {
  const { data } = await AxiosInstance.post(`/v1/tenants/reports/new`, values);
  return data;
};

export const getTenantsPaginated = async (pagination: any) => {
  const { data } = await AxiosInstance.get(`/v1/tenants/persons`, {
    params: {
      limit: pagination.pageSize,
      offset: (pagination.current - 1) * pagination.pageSize,
    },
  });
  return data;
};

export const getTenant = async (id: any) => {
  const { data } = await AxiosInstance.get(`/v1/tenants/persons/${id}`, {});
  return data;
};

export const deleteTenant = async (tenantId: any) => {
  await AxiosInstance.patch(`/v1/tenants/persons/disable/${tenantId}`);
};

export const updateTenant = async (
  beforeUpdateTenant: TenantPerson,
  tenantId: any,
  queryParams?: object,
) => {
  const payload: TenantPerson & {
    rfc?: TenantPerson["rfc"];
    company_rfc?: TenantPerson["company_rfc"];
    legal_entity_name?: TenantPerson["legal_entity_name"];
  } = { ...beforeUpdateTenant };

  if (payload.legal_type === "1") {
    // natural
    delete payload.company_rfc;
    delete payload.legal_entity_name;
  } else if (payload.legal_type === "2") {
    // legal
    payload.rfc = payload.company_rfc;
  }

  const { legal_type: _ignoredLegalType, ...tenantData } = payload;

  return await AxiosInstance.patch(
    `/v1/tenants/persons/update/${tenantId}`,
    tenantData,
    {
      params: queryParams,
    },
  );
};

export const createTenant = async (values: TenantPerson) => {
  if (values.legal_type === "1") {
    // If legal_type is "1" (company), ignore rfc
    delete values.company_rfc;
    delete values.legal_entity_name;
  } else if (values.legal_type === "2") {
    // If legal_type is "2" (individual), ignore company_rfc and company_name
    values.rfc = values.company_rfc;
  }
  const { data } = await AxiosInstance.post(`/v1/tenants/persons/new`, {
    name: values.name,
    first_surname: values.first_surname,
    second_surname: values.second_surname,
    phone_number: values.phone_number,
    dial_code: values.dial_code,
    birth_date: values.birth_date,
    email: values.email,
    legal_type: values.legal_type,
    curp: values.curp,
    rfc: values.rfc,
    legal_entity_name: values.legal_entity_name,
  });
  return data;
};

export const getOrganizationTenants = async (pagination: any) => {
  const { data } = await AxiosInstance.get(`/v1/tenants/persons/organization`, {
    params: {
      limit: pagination.pageSize,
      offset: (pagination.current - 1) * pagination.pageSize,
    },
  });
  return data;
};

export const getOrganizationProperties = async (pagination: any) => {
  const { data } = await AxiosInstance.get(
    `/v1/tenants/properties/organization`,
    {
      params: {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize,
      },
    },
  );
  return data;
};

export const getTenantReports = async (
  pagination: { pageSize: number; current: number },
  filter: string = "",
  additionalFilters: {
    is_active?: string;
    status?: string;
    legal_type?: string;
  } = {},
) => {
  const params: any = {
    limit: pagination.pageSize,
    offset: (pagination.current - 1) * pagination.pageSize,
  };

  if (additionalFilters.is_active) {
    params.is_active = additionalFilters.is_active;
  }
  if (additionalFilters.status) {
    params.status = additionalFilters.status;
  }
  if (additionalFilters.legal_type) {
    params.legal_type = additionalFilters.legal_type;
  }

  const { data } = await AxiosInstance.get(
    `/v1/tenants/reports?filter=${filter}`,
    { params },
  );
  return data;
};

export const getTenantReportsByPropertyId = async (
  pagination: any,
  propertyId: string,
  organizationScope: boolean = false,
) => {
  const endpoint = organizationScope
    ? `/v1/tenants/reports/organization`
    : `/v1/tenants/reports`;

  const { data } = await AxiosInstance.get(endpoint, {
    params: {
      property_id: propertyId,
      limit: pagination.pageSize,
      offset: (pagination.current - 1) * pagination.pageSize,
    },
  });
  return data;
};

export const getContractEditorData = async (
  folderId: string,
  options?: {
    force?: boolean;
    /**
     * "Si este folio ya tiene contrato, el 400 es parte de mi flujo y yo lo
     * manejo".
     *
     * Lo declara UNICAMENTE el call site que de verdad lo maneja
     * (`useContractEditorData`, el modo create-from-folio), y la tabla de
     * `expectedBusinessResponses.ts` lo exige para no reportar ese 400. El
     * MISMO 400 desde cualquier otro call site —el boton "Actualizar datos del
     * multiexpediente", donde es un bug— llega sin la marca y se sigue viendo
     * en Sentry. Ver `expectedBusinessResponseMark.ts`.
     */
    expectFolioAlreadyHasContract?: boolean;
  },
) => {
  const params = options?.force ? { force: "true" } : undefined;
  const config: AxiosRequestConfig = { params };

  if (options?.expectFolioAlreadyHasContract) {
    markExpectedBusinessResponse(
      config,
      "editor-data-folio-already-has-contract",
    );
  }

  const { data } = await AxiosInstance.get(
    `/v1/tenants/reports/${folderId}/contract/editor-data`,
    config,
  );
  return data;
};

export const getTenantReportDetails = async (reportId: string) => {
  const { data } = await AxiosInstance.get(
    `/v1/tenants/reports/${reportId}/details`,
  );
  return data;
};

export const getFile = async (tenantReportId: string, fileId: string) => {
  const { data } = await AxiosInstance.get(
    `/v1/tenants/reports/${tenantReportId}/requirements/file/${fileId}/download`,
    {
      responseType: "blob",
    },
  );
  return data;
};

export const downloadTenantMultireport = async (reportId: string) => {
  return await AxiosInstance.post(
    `/v1/tenants/reports/download/${reportId}`,
    null,
    {
      responseType: "blob",
    },
  );
};

export const getTenantMultireportPublicLink = async (
  reportId: string,
  regenerate: boolean = false,
) => {
  const { data: r } = await AxiosInstance.get(
    `/v1/tenants/reports/${reportId}/multireport/link`,
    {
      params: { regenerate },
    },
  );
  return r;
};

export const getTenantMultireportWebData = async (reportId: string) => {
  const { data } = await AxiosInstance.get(
    `/v1/tenants/reports/${reportId}/multireport-web`,
  );
  return data;
};

export const getTenantMultireportPublicData = async (
  token: string,
  email?: string,
) => {
  const params: any = { token, email };

  const { data: r } = await AxiosInstance.get(
    `/v1/tenants/reports/multireport/public`,
    {
      params,
    },
  );
  return r;
};

export const downloadTenantMultireportExpress = async (reportId: string) => {
  try {
    return await AxiosInstance.post(
      `/v1/tenants/reports/download/${reportId}/express`,
      null,
      {
        responseType: "blob",
      },
    );
  } catch (error) {
    // If we received JSON instead of a blob, it means there's an error message
    if (
      error.response &&
      error.response.headers["content-type"]?.includes("application/json")
    ) {
      // Re-throw with the parsed data for better error handling
      throw error;
    }
    throw error;
  }
};

export const downloadGuarantorMultireportExpress = async (reportId: string) => {
  try {
    return await AxiosInstance.post(
      `/v1/tenants/reports/download/${reportId}/guarantor-express`,
      null,
      {
        responseType: "blob",
      },
    );
  } catch (error) {
    // If we received JSON instead of a blob, it means there's an error message
    if (
      error.response &&
      error.response.headers["content-type"]?.includes("application/json")
    ) {
      // Re-throw with the parsed data for better error handling
      throw error;
    }
    throw error;
  }
};

export const downloadTenantFolderZip = async (tenantReportId: string) => {
  const { data } = await AxiosInstance.get(
    `/v1/tenants/reports/${tenantReportId}/folder/download`,
    {
      responseType: "blob",
    },
  );
  return data;
};

export const activateReport = async (reportId: string) => {
  const { data } = await AxiosInstance.put(
    `/v1/tenants/reports/enable/${reportId}`,
  );
  return data;
};

export const deactivateReport = async (reportId: string) => {
  const { data } = await AxiosInstance.put(
    `/v1/tenants/reports/disable/${reportId}`,
  );
  return data;
};

export const sendTenantMultireport = async (reportId: string) => {
  const { data } = await AxiosInstance.post(
    `/v1/tenants/reports/send/${reportId}`,
  );
  return data;
};

export const sendTenantMultireportExpress = async (reportId: string) => {
  const { data } = await AxiosInstance.post(
    `/v1/tenants/reports/send/${reportId}/express`,
  );
  return data;
};

export const addGuarantorInvestigation = async (reportId: string) => {
  const { data } = await AxiosInstance.post(
    `/v1/tenants/reports/${reportId}/add-guarantor-investigation`,
  );
  return data;
};

export const addReportPerson = async (
  reportId: string,
  payload: {
    role: "tenant" | "guarantor" | "landlord";
    person: Record<string, any>;
  },
) => {
  const { data } = await AxiosInstance.post(
    `/v1/tenants/reports/${reportId}/participants`,
    payload,
  );
  return data;
};

export const sendTenantSignature = async (formData: FormData) => {
  const { data } = await AxiosInstance.post(`/v1/tenants/signature`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
};
